import { TDrawAndPrizeResult } from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import CardWithBackgroundImage from 'components/CardWithBackgroundImage';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import Separator from 'components/Separator';
import Subheading from 'components/Subheading';
import { useEffect, useState } from 'react';
import { drawApiClient } from 'utils/apiClient';
import { stringFromDate } from 'utils/common';
import { colors } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

interface AllResultsProps {
  id: number;
  prizeTitle: string;
  prizeHeroImage: string;
  drawDate: Date | null;
  drawType: string | null;
  borderColor: string;
  winningLine?: string;
  proofUrl?: string;
}

const AllResults = ({
  id,
  prizeTitle,
  prizeHeroImage,
  drawDate,
  drawType,
  winningLine,
  proofUrl,
  borderColor,
}: AllResultsProps) => {
  return (
    <div id={id.toString()} className="szMinilottoResult flexRow">
      <CardWithBackgroundImage src={prizeHeroImage} />

      <div className="rightSection flexLeftAligned">
        <p className="title">{prizeTitle}</p>
        <p className="drawDate">
          {drawDate ? stringFromDate(new Date(drawDate)) : ''}
        </p>
        <p className="drawCode" style={{ marginTop: '0.8em' }}>
          {drawType}
        </p>

        <Separator />

        <div className="fullWidth flexRow flexSpaceBetween">
          <p className="winningLine">{winningLine}</p>
          {proofUrl && (
            <p className="proofLink">
              <a href={proofUrl} target="_blank">
                Draw
              </a>
            </p>
          )}
        </div>
      </div>

      <style jsx>
        {`
          .szMinilottoResult {
            width: 100%;
            border: 3px solid ${borderColor};
            border-radius: 5px;
            box-shadow: 2px 2px 3px ${colors.primaryBrandGray}50;
          }

          :global(.szMinilottoResult .cardWithBackgroundImage) {
            width: 35%;
          }

          .rightSection {
            padding: 5%;
            width: 65%;
          }

          .szMinilottoResult p {
            font-family: 'Press Start 2P', Montserrat, sans-serif;
          }

          .title {
            text-transform: uppercase;
            margin-bottom: 0.5em;
          }

          .drawDate {
            font-size: 1em;
            margin: 0.5em 0;
          }

          .drawCode {
            font-size: 0.8em;
            margin: 0;
          }

          .winningLine {
            font-size: 1.5em;
            text-transform: uppercase;
            margin: 0;
          }

          .proofLink {
            font-size: 0.9em;
            text-decoration: underline;
          }
        `}
      </style>
    </div>
  );
};

const TokenTownResults = () => {
  const [results, setResults] = useState<TDrawAndPrizeResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const getTokenTownResults = async () => {
    if (!results) {
      setLoading(true);
      const fetchTTResults = await drawApiClient.public.getTokenTownResults();

      if (fetchTTResults.status === HttpStatusCode.OK) {
        setResults(fetchTTResults.body);
      }
      setLoading(false);
    }
  };

  const getDrawType = (drawCode: string) => {
    if (drawCode === 'DAILY_POUND') {
      return 'Daily Raffle';
    }

    if (
      drawCode === 'DAILY_CHALLENGER' ||
      drawCode === 'DAILY_CHALLENGER_40_5'
    ) {
      return 'Daily Fixed-Odds Draw';
    }

    const szMatcher = new RegExp(/^SZ_/);
    if (szMatcher.test(drawCode)) {
      return 'Fixed-Odds Raffle';
    }

    return '';
  };

  useEffect(() => {
    async function triggerUpdateResults() {
      await getTokenTownResults();
    }
    triggerUpdateResults();
  }, []);

  return (
    <PaddedMaxWidthContainer additionalClassName="tokenTownResults">
      <Subheading
        underlineColor="transparent"
        style={{ marginBottom: 0, marginTop: '1em' }}
      >
        Token Town
      </Subheading>
      <Subheading style={{ marginTop: 0 }}>Mini Game Results</Subheading>

      <p style={{ textAlign: 'center' }}>
        Find the winning entries for all the Subscriber Zone mini games right
        here
      </p>

      {results && !loading && (
        <div className="results">
          {results.map((result, idx) => {
            const drawType = getDrawType(result.drawSchedule.code);

            return (
              <AllResults
                key={idx}
                id={idx}
                prizeTitle={result.prize.title}
                prizeHeroImage={result.prize.imageLocations.main}
                drawDate={result.draw.drawDate}
                drawType={drawType}
                winningLine={
                  result.draw.result?.type === GameType.RAFFLE
                    ? `Raffle: ${result.draw.result.raffle}`
                    : result.draw.result?.type === GameType.LOTTERY
                      ? `Numbers: ${result.draw.result.main.join(', ')}`
                      : ''
                }
                proofUrl={
                  result.draw.result?.type === GameType.RAFFLE
                    ? result.draw.result?.raffleAdditionalInfo
                        ?.verificationPageLink
                    : result.draw.result?.type === GameType.LOTTERY
                      ? result.draw.result?.mainAdditionalInfo
                          ?.verificationPageLink
                      : undefined
                }
                borderColor={
                  result.drawSchedule.code === 'DAILY_CHALLENGER' ||
                  result.drawSchedule.code === 'DAILY_CHALLENGER_40_5'
                    ? '#FB55B0'
                    : '#50E3C2'
                }
              />
            );
          })}
        </div>
      )}

      {!results && loading && (
        <div className="spinner-wrapper">
          <PlainLoadingSpinner />
        </div>
      )}

      <style jsx>
        {`
          .results {
            margin: 2em 0;
            width: 100%;
            display: grid;
            grid-template-columns: 1fr;
            grid-gap: 1em;
          }

          .spinner-wrapper {
            margin: 0 auto;
            padding: 4em 0;
          }

          @media (min-width: 700px) {
            .results {
              grid-template-columns: repeat(2, 1fr);
            }
          }
        `}
      </style>
    </PaddedMaxWidthContainer>
  );
};

export default TokenTownResults;
